import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import type { SxProps, Theme } from '@mui/material';
import type { SelectProps } from 'components/common';
import { CommonSelect, PlaceholderNoAccess } from 'components/common';
import { type SelectFlowsParams, useInfiniteFlows } from '../api';
import type { Flow } from '../types';
import { useInView } from 'react-intersection-observer';
import { usePermissionGuard } from 'features/login';
import { trackCabinetQueryError } from 'lib/datadog';

interface SelectFlowsSx {
  /** Merged into `.MuiAutocomplete-root` styles when `asAutocomplete` is used. */
  root?: SxProps<Theme>;
}

interface SelectFlowsAttrs {
  open: boolean;
  multiples: boolean;
  placeholder: string;
  params?: SelectFlowsParams;
  /** Defaults to flow id. Use `name` when the consumer stores a string flow name. */
  valueKey?: 'id' | 'name';
  sx?: SelectFlowsSx;
}

type SelectFlowsProps = Partial<
  Pick<
    SelectProps,
    | 'handleChange'
    | 'name'
    | 'inputLabel'
    | 'onClose'
    | 'disabled'
    | 'asAutocomplete'
    | 'size'
    | 'hideErrorMessage'
    | 'required'
  > &
  SelectFlowsAttrs
>;

const SelectFlowsComponent: FC<SelectFlowsProps> = (
  {
    handleChange,
    name,
    inputLabel,
    multiples,
    placeholder,
    onClose,
    open,
    params,
    disabled,
    asAutocomplete,
    valueKey = 'id',
    size,
    sx,
    hideErrorMessage,
    required,
  }) => {
  const { ref, inView } = useInView({
    threshold: 0,
  });
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError, error } =
    useInfiniteFlows({ params });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (!isError || !error) {
      return;
    }

    trackCabinetQueryError(error, 'facebook', 'flow');
  }, [isError, error]);

  const options = useMemo(() => {
    if (!data) return [];
    return data.pages.flatMap((page) =>
      page.data.map((flow: Flow) => ({
        label: flow.name,
        value: valueKey === 'name' ? flow.name : flow.id,
      })),
    );
  }, [data, valueKey]);

  return (
    <CommonSelect
      name={name ?? 'flows'}
      inputLabel={inputLabel ?? 'Flows'}
      options={options}
      handleChange={handleChange}
      placeholder={placeholder}
      multiples={multiples}
      onClose={onClose}
      open={open}
      loading={isFetchingNextPage}
      loadMoreRef={ref}
      disabled={disabled}
      asAutocomplete={asAutocomplete}
      size={size}
      sx={sx?.root}
      hideErrorMessage={hideErrorMessage}
      required={required}
    />
  );
};

const SelectFlows: FC<SelectFlowsProps> = (props) => {
  const { accessToViewFlow } = usePermissionGuard();
  if (!accessToViewFlow)
    return <PlaceholderNoAccess title={'No access to view flow'} />;

  return <SelectFlowsComponent {...props} />;
};

export default SelectFlows;
